import { useEffect } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { HOST_MB } from '@/lib/constants';
import { useContracts } from '@/hooks/api/use-contracts';
import { hasContractApporteur43397 } from '@/lib/contracts';
import { queryKeys } from '@/lib/query-keys';
import type { User, Whitelabel } from '@/types/api/user';
import { LightenDarkenColor } from '@/lib/helpers';

/**
 * Lecture du cache seule (`skipToken`) : monté hors de `App`, avant le chargement des routes du
 * Pass, ce composant ne doit déclencher aucun appel. Il suit les données chargées par les écrans.
 */
const useCachedQuery = <T,>(queryKey: readonly unknown[]) => useQuery<T>({ queryKey, queryFn: skipToken }).data;

const AM_GESTION_PRIMARY = '#639E30';

const ThemeOverride = () => {
    const contracts = useContracts(false).data?.data;
    const hasApporteur43397 = hasContractApporteur43397(contracts);
    const contractsLoaded = Array.isArray(contracts);
    // Marque blanche courante : celle de l'utilisateur une fois chargée, sinon celle de l'hôte
    // (écrans de connexion).
    const user = useCachedQuery<User>(queryKeys.user);
    const userWhitelabel = useCachedQuery<Whitelabel>(queryKeys.whitelabel(user?.whitelabel));
    const hostWhitelabel = useCachedQuery<Whitelabel>(queryKeys.whitelabelByHost(HOST_MB));
    const styleColor1 = (userWhitelabel ?? hostWhitelabel)?.style_color1;

    useEffect(() => {
        const root = document.documentElement;

        const apiColor = styleColor1 && styleColor1.toLowerCase() !== '#000000' ? styleColor1 : null;
        const isAmGestion = HOST_MB !== 43397;
        const color = isAmGestion ? (apiColor || AM_GESTION_PRIMARY) : apiColor;
        const shouldApply = color && (isAmGestion || (contractsLoaded && !hasApporteur43397));

        if (shouldApply) {
            root.style.setProperty('--color-primary', color);
            root.style.setProperty('--color-primary-hover', LightenDarkenColor(color, -0.3));
            root.style.setProperty('--color-ring', color);
        } else {
            root.style.removeProperty('--color-primary');
            root.style.removeProperty('--color-primary-hover');
            root.style.removeProperty('--color-ring');
        }

        return () => {
            root.style.removeProperty('--color-primary');
            root.style.removeProperty('--color-primary-hover');
            root.style.removeProperty('--color-ring');
        };
    }, [contractsLoaded, hasApporteur43397, styleColor1]);

    return null;
};

export default ThemeOverride;
